import { type IProject, PROJECT_STATUS } from '@/types';
import { type SSEController, subscribeToSse } from './sse.util';

interface ProjectSSEHandlers {
  setIsLoading: (loading: boolean) => void;
  setProgress: (project: IProject, progress: number) => void;
  onSuccess?: (project: IProject) => void;
  onError?: (errorMessage: string) => void;
  /** The run did not fail but can no longer be followed: cancelled, or the stream ended early. */
  onInterrupted?: (reason: ProjectSSEInterruption) => void;
}

export type ProjectSSEInterruption = 'cancelled' | 'watch-lost';

export function subscribeToProjectSSE(
  projectId: string,
  handlers: ProjectSSEHandlers,
): SSEController {
  handlers.setIsLoading(true);

  return subscribeToSse<IProject>(
    { url: `projects/${projectId}` },
    {
      onConnect() {
        // Optional: handle connection
      },
      onData(project, stop) {
        if (project.status === PROJECT_STATUS.FAILED) {
          handlers.onError?.(`${project.error_message} projectId: ${project.id}`);
          handlers.setIsLoading(false);
          stop(); // Stop SSE on error
        }

        if (project.status === PROJECT_STATUS.CANCELLED) {
          handlers.onInterrupted?.('cancelled');
          handlers.setIsLoading(false);
          stop(); // The backend closes the stream on cancelled too
        }

        if (project.status === PROJECT_STATUS.COMPLETED) {
          handlers.onSuccess?.(project);
          handlers.setIsLoading(false);
          stop(); // Stop SSE on completion
        }

        handlers.setProgress(project, Math.round(project.progress * 100));
      },
      onError(_error, stop) {
        // The job may still be running, so this is not a failure, but the caller must settle or
        // the run it tracks never clears.
        handlers.onInterrupted?.('watch-lost');
        handlers.setIsLoading(false);
        stop();
      },
      onDisconnect() {
        // Cleanup flags
      },
    },
    5000, // auto-reconnect delay
  );
}
